import {
  getCountries,
  getCountryCallingCode,
  type CountryCode as LibCountryCode,
} from "libphonenumber-js";

import { COUNTRY_NAMES } from "./country-names";
import type { PhoneNumberFieldCountry } from "./types";

// Pinned to a commit (the repo has no tags), so a change upstream can't move
// the flags (PhoneNumberField #19)
const FLAG_CDN_BASE_URL =
  "https://cdn.jsdelivr.net/gh/hampusborgos/country-flags@c09927e63705529bbf59ca6684cd9b23225dddad/svg/";

function flagUrlFromCountryCode(code: string): string {
  return `${FLAG_CDN_BASE_URL}${code.toLowerCase()}.svg`;
}

// Not Intl.DisplayNames: Hermes (iOS and Android) has none, so native showed
// ISO codes (molecules audit 2026-10-30, PhoneNumberField #1)
function getCountryName(code: LibCountryCode): string {
  return COUNTRY_NAMES[code];
}

function createCountry(code: LibCountryCode): PhoneNumberFieldCountry {
  return {
    code,
    name: getCountryName(code),
    dialCode: `+${getCountryCallingCode(code)}`,
    flagUrl: flagUrlFromCountryCode(code),
  };
}

/** ISO 3166-1 alpha-2 codes for the ASEAN member states. */
const ASEAN_COUNTRY_CODES: LibCountryCode[] = [
  "SG",
  "MY",
  "ID",
  "PH",
  "TH",
  "VN",
  "BN",
  "KH",
  "LA",
  "MM",
];

/**
 * Build a country list where `pinnedCodes` are hoisted to the top (in the given
 * order) and everything else is sorted alphabetically by name.
 */
function buildCountryList(
  codes: LibCountryCode[],
  pinnedCodes: LibCountryCode[],
): PhoneNumberFieldCountry[] {
  const pinnedRank = new Map(pinnedCodes.map((code, index) => [code, index]));

  return codes.map(createCountry).sort((a, b) => {
    const aRank = pinnedRank.get(a.code);
    const bRank = pinnedRank.get(b.code);

    if (aRank !== undefined && bRank !== undefined) return aRank - bRank;
    if (aRank !== undefined) return -1;
    if (bRank !== undefined) return 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Every country libphonenumber-js can format, with the ASEAN markets pinned to
 * the top (SG first) and the rest sorted alphabetically by name. The full list
 * lets international numbers (e.g. +1) resolve their country; the native picker
 * adds a search box to make the long tail reachable.
 */
export const DEFAULT_PHONE_NUMBER_FIELD_COUNTRIES: PhoneNumberFieldCountry[] = buildCountryList(
  getCountries(),
  ASEAN_COUNTRY_CODES,
);

/**
 * ASEAN-only preset (SG first, the rest alphabetical). Pass to the `countries`
 * prop when the field should be scoped to ASEAN dial codes:
 *
 * ```tsx
 * <PhoneNumberField countries={ASEAN_PHONE_NUMBER_FIELD_COUNTRIES} />
 * ```
 */
export const ASEAN_PHONE_NUMBER_FIELD_COUNTRIES: PhoneNumberFieldCountry[] = buildCountryList(
  getCountries().filter((code): code is LibCountryCode => ASEAN_COUNTRY_CODES.includes(code)),
  ASEAN_COUNTRY_CODES,
);
